/**
 * 工具属性栏（M2-05）。控件由激活工具的选项 schema 生成；
 * 值的读写全部回调给交互层，UI 不存工具状态。
 */
import type { JSX } from 'solid-js';
import { For, Show, Switch, Match } from 'solid-js';
import type { ToolDefinition, ToolOptionValue } from '../../interaction/tools/registry';
import styles from './Toolbar.module.css';

export interface OptionsBarProps {
  readonly tool: ToolDefinition;
  readonly value: (key: string) => ToolOptionValue;
  readonly onChange: (key: string, value: ToolOptionValue) => void;
  /**
   * 给某个 `enum` 选项换一份动态的候选列表（目前只有文字工具的
   * `fontFamily` 用得上——字体是运行时才知道的，`registry.ts` 里的
   * `choices` 只是加载完成前的兜底）。返回 `undefined` 就照常用
   * `schema.choices`。
   */
  readonly choicesOverride?: (
    key: string,
  ) => ReadonlyArray<{ readonly value: string; readonly label: string }> | undefined;
}

export function OptionsBar(props: OptionsBarProps): JSX.Element {
  return (
    <div class={styles.options}>
      <span class={styles.toolName} data-testid="active-tool-name">
        {props.tool.label}
      </span>
      <For each={props.tool.options}>
        {(option) => (
          <label class={styles.option}>
            <span class={styles.optionLabel}>{option.label}</span>
            <Switch>
              <Match when={option.kind === 'number' ? option : null} keyed>
                {(schema) => (
                  <>
                    {/* 画笔大小照 PS 惯例配一根滑竿：数字框只能敲准确值、
                        拖着调没有滑竿顺手——只给 'size' 加，硬度/不透明度
                        这些没被用户提过，不擅自都改成滑竿 */}
                    <Show when={schema.key === 'size'}>
                      <input
                        type="range"
                        class={styles.sizeSlider}
                        min={schema.min}
                        max={schema.max}
                        step={schema.step}
                        value={String(props.value(schema.key))}
                        aria-label={schema.label}
                        data-testid={`tool-option-${schema.key}-slider`}
                        onInput={(event) => {
                          props.onChange(schema.key, Number(event.currentTarget.value));
                        }}
                      />
                    </Show>
                    <input
                      type="number"
                      class={styles.numberInput}
                      min={schema.min}
                      max={schema.max}
                      step={schema.step}
                      value={String(props.value(schema.key))}
                      data-testid={`tool-option-${schema.key}`}
                      onChange={(event) => {
                        const parsed = Number(event.currentTarget.value);
                        if (Number.isFinite(parsed)) props.onChange(schema.key, parsed);
                      }}
                    />
                    <span class={styles.unit}>{schema.unit ?? ''}</span>
                  </>
                )}
              </Match>
              <Match when={option.kind === 'boolean' ? option : null} keyed>
                {(schema) => (
                  <input
                    type="checkbox"
                    checked={props.value(schema.key) === true}
                    data-testid={`tool-option-${schema.key}`}
                    onChange={(event) => {
                      props.onChange(schema.key, event.currentTarget.checked);
                    }}
                  />
                )}
              </Match>
              <Match when={option.kind === 'enum' ? option : null} keyed>
                {(schema) => (
                  <select
                    class={styles.select}
                    value={String(props.value(schema.key))}
                    data-testid={`tool-option-${schema.key}`}
                    onChange={(event) => {
                      props.onChange(schema.key, event.currentTarget.value);
                    }}
                  >
                    <For each={props.choicesOverride?.(schema.key) ?? schema.choices}>
                      {(choice) => <option value={choice.value}>{choice.label}</option>}
                    </For>
                  </select>
                )}
              </Match>
              <Match when={option.kind === 'buttonGroup' ? option : null} keyed>
                {(schema) => (
                  <div class={styles.buttonGroup} role="group" aria-label={schema.label}>
                    <For each={schema.choices}>
                      {(choice) => (
                        <button
                          type="button"
                          class={styles.buttonGroupItem}
                          classList={{
                            [styles.buttonGroupItemActive!]: props.value(schema.key) === choice.value,
                          }}
                          title={choice.title ?? choice.label}
                          aria-pressed={props.value(schema.key) === choice.value}
                          data-testid={`tool-option-${schema.key}-${choice.value}`}
                          onClick={() => {
                            props.onChange(schema.key, choice.value);
                          }}
                        >
                          {choice.label}
                        </button>
                      )}
                    </For>
                  </div>
                )}
              </Match>
            </Switch>
          </label>
        )}
      </For>
    </div>
  );
}
